import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useCurriculum } from "@/context/CurriculumContext";
import { toast } from "sonner";
import { Modal, Field } from "@/pages/Dashboard";
import { SUBJECT_META } from "@/lib/scale";
import { ChevronLeft, Users2, Target, Plus, Trash2, Loader2, UserCog, Sparkles, Copy, UserPlus2, Map } from "lucide-react";

export default function GroupDetail() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const curriculum = useCurriculum();
  const [group, setGroup] = useState(null);
  const [allStudents, setAllStudents] = useState([]);
  const [targetModal, setTargetModal] = useState(false);
  const [membersModal, setMembersModal] = useState(false);
  const [inviteModal, setInviteModal] = useState(false);

  const load = useCallback(() => {
    api.get(`/groups/${groupId}`).then((r) => setGroup(r.data)).catch(() => navigate("/groups"));
  }, [groupId, navigate]);
  useEffect(() => {
    load();
    api.get("/students").then((r) => setAllStudents(r.data)).catch(() => {});
  }, [load]);

  if (!group || !curriculum) return <div className="flex justify-center py-24 text-slate-300"><Loader2 className="animate-spin" /></div>;
  const subjects = curriculum.subjects;

  const delTarget = async (id) => { await api.delete(`/group-targets/${id}`); toast.success("Shared target removed"); load(); };

  return (
    <div>
      <button data-testid="group-back-btn" onClick={() => navigate("/groups")} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 transition mb-5">
        <ChevronLeft size={16} /> Groups
      </button>
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div className="flex items-center gap-4">
          <span className="h-16 w-16 rounded-3xl bg-[#9A7AA0] text-white flex items-center justify-center"><Users2 size={28} /></span>
          <div>
            <h1 className="font-['Outfit'] text-4xl font-bold text-slate-800 tracking-tight">{group.name}</h1>
            <p className="text-slate-500 mt-0.5">{group.members.length} students across classes</p>
          </div>
        </div>
        <button data-testid="group-map-btn" onClick={() => navigate(`/group/${groupId}/map`)} className="bg-white border border-slate-200 hover:border-[#44546A] text-slate-700 rounded-full px-5 py-2.5 font-semibold font-['Outfit'] transition flex items-center gap-2">
          <Map size={17} /> Map
        </button>
        <button data-testid="invite-colleague-btn" onClick={() => setInviteModal(true)} className="bg-white border border-slate-200 hover:border-[#6C8EBF] text-slate-700 rounded-full px-5 py-2.5 font-semibold font-['Outfit'] transition flex items-center gap-2">
          <UserPlus2 size={17} /> Invite colleague
        </button>
        <button data-testid="manage-members-btn" onClick={() => setMembersModal(true)} className="bg-white border border-slate-200 hover:border-[#9A7AA0] text-slate-700 rounded-full px-5 py-2.5 font-semibold font-['Outfit'] transition flex items-center gap-2">
          <UserCog size={17} /> Manage members
        </button>
      </div>

      {/* Members */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
        {group.members.map((m) => (
          <button key={m.id} data-testid={`group-member-${m.id}`} onClick={() => navigate(`/student/${m.id}`)}
            className="text-left bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(42,59,71,0.04)] p-4 flex items-center gap-3 hover:shadow-[0_8px_30px_rgba(42,59,71,0.08)] transition">
            <span className="h-10 w-10 rounded-xl bg-[#4A5D6B] text-white flex items-center justify-center font-['Outfit'] font-bold">{m.name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}</span>
            <div className="min-w-0"><p className="font-['Outfit'] font-semibold text-slate-800 truncate">{m.name}</p><p className="text-xs text-slate-400 truncate">{m.class_name}</p></div>
          </button>
        ))}
      </div>

      {/* Shared targets */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(42,59,71,0.04)] p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-['Outfit'] text-2xl font-bold text-slate-800 flex items-center gap-2"><Target size={22} className="text-[#9A7AA0]" /> Shared Group Targets</h2>
          <button data-testid="add-group-target-btn" onClick={() => setTargetModal(true)} className="bg-[#9A7AA0]/15 hover:bg-[#9A7AA0]/25 text-[#7a5e80] rounded-full px-4 py-2 font-semibold font-['Outfit'] text-sm transition flex items-center gap-1.5"><Plus size={16} /> Add shared target</button>
        </div>
        {group.group_targets.length === 0 ? (
          <p className="text-slate-400 text-sm py-6 text-center">No shared targets yet. A shared target is copied to every member as an editable priority target.</p>
        ) : (
          <div className="space-y-2.5">
            {group.group_targets.map((t) => {
              const meta = SUBJECT_META[t.subject];
              const dom = subjects[t.subject]?.domains.find((d) => d.key === t.domain);
              return (
                <div key={t.id} data-testid={`group-target-${t.id}`} className="group flex items-start gap-3 p-3.5 rounded-xl border border-slate-100">
                  <span className="mt-1 h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: meta?.tint }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-slate-800 font-medium">{t.text}</p>
                    <p className="text-xs mt-1"><span className="font-bold uppercase tracking-wide" style={{ color: meta?.tint }}>{meta?.name}</span><span className="text-slate-400"> · {dom?.name}{t.target_level ? ` · target level ${t.target_level}` : ""} · applied to {group.members.length} students</span></p>
                  </div>
                  <button data-testid={`delete-group-target-${t.id}`} onClick={() => delTarget(t.id)} className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition"><Trash2 size={15} /></button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {targetModal && <GroupTargetModal groupId={groupId} subjects={subjects} memberCount={group.members.length} onClose={() => setTargetModal(false)} onSaved={() => { setTargetModal(false); load(); }} />}
      {membersModal && <MembersModal group={group} allStudents={allStudents} onClose={() => setMembersModal(false)} onSaved={() => { setMembersModal(false); load(); }} />}
      {inviteModal && <GroupInviteModal groupId={groupId} onClose={() => setInviteModal(false)} />}
    </div>
  );
}

function GroupInviteModal({ groupId, onClose }) {
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const invite = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post(`/groups/${groupId}/invite`, { email });
      toast.success(data.pending ? "Invite saved — they'll get access when they sign up" : "Colleague added to the group");
      onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Invite a colleague to this group" onClose={onClose}>
      <form onSubmit={invite} className="space-y-4">
        <p className="text-sm text-slate-500">They'll be able to view and edit this support group and its shared targets.</p>
        <Field label="Colleague's email">
          <input data-testid="group-invite-email-input" type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="colleague@school.ie" className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-[#81B29A]" />
        </Field>
        <button data-testid="send-group-invite-btn" disabled={saving} className="w-full bg-[#4A5D6B] hover:bg-[#3A4A56] text-white rounded-full py-3 font-semibold font-['Outfit'] flex items-center justify-center gap-2 disabled:opacity-60">
          {saving && <Loader2 size={18} className="animate-spin" />} Send invite
        </button>
      </form>
    </Modal>
  );
}

function GroupTargetModal({ groupId, subjects, memberCount, onClose, onSaved }) {
  const keys = Object.keys(subjects);
  const [subject, setSubject] = useState(keys[0]);
  const [domain, setDomain] = useState(subjects[keys[0]].domains[0].key);
  const [text, setText] = useState("");
  const [level, setLevel] = useState("");
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [pullList, setPullList] = useState(null); // null=hidden, []=loaded
  const domains = subjects[subject].domains;

  const applyTarget = (subj, dom, txt, lvl) => {
    if (subjects[subj]) { setSubject(subj); setDomain(subjects[subj].domains.some((d) => d.key === dom) ? dom : subjects[subj].domains[0].key); }
    setText(txt || "");
    setLevel(lvl ? String(lvl) : "");
  };

  const suggestAI = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post(`/groups/${groupId}/suggest-target?subject=${subject}`);
      applyTarget(data.subject, data.domain, data.text, data.target_level);
      toast.success(`AI suggested a ${subjects[subject].name} target — review & edit before adding`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAiLoading(false); }
  };

  const openPull = async () => {
    if (pullList !== null) { setPullList(null); return; }
    try { const { data } = await api.get(`/groups/${groupId}/member-targets`); setPullList(data); }
    catch (e) { toast.error("Could not load member targets"); }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/groups/${groupId}/targets`, { subject, domain, text, target_level: level ? Number(level) : null });
      toast.success(`Shared target added to ${memberCount} students`);
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="Add a shared group target" onClose={onClose}>
      <div className="flex gap-2 mb-4">
        <button type="button" data-testid="gt-suggest-ai-btn" onClick={suggestAI} disabled={aiLoading}
          className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-2 text-sm font-semibold font-['Outfit'] text-white transition disabled:opacity-50"
          style={{ background: "linear-gradient(135deg,#9A7AA0,#7a5e80)" }}>
          {aiLoading ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Suggest with AI
        </button>
        <button type="button" data-testid="gt-pull-btn" onClick={openPull}
          className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-2 text-sm font-semibold font-['Outfit'] bg-white border border-slate-200 text-slate-700 hover:border-[#9A7AA0] transition">
          <Copy size={15} /> {pullList !== null ? "Hide member targets" : "Pull from a member"}
        </button>
      </div>

      {pullList !== null && (
        <div data-testid="gt-pull-list" className="mb-4 max-h-44 overflow-y-auto border border-slate-100 rounded-xl divide-y divide-slate-50">
          {pullList.length === 0 ? (
            <p className="text-sm text-slate-400 p-3 text-center">No existing targets on group members yet.</p>
          ) : pullList.map((mt, i) => (
            <button key={`${mt.student_name}-${mt.subject}-${i}`} type="button" data-testid={`gt-pull-item-${i}`}
              onClick={() => { applyTarget(mt.subject, mt.domain, mt.text, mt.target_level); setPullList(null); }}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 transition">
              <p className="text-sm text-slate-700">{mt.text}</p>
              <p className="text-xs text-slate-400 mt-0.5">{SUBJECT_META[mt.subject]?.name} · from {mt.student_name}</p>
            </button>
          ))}
        </div>
      )}

      <form onSubmit={save} className="space-y-4">
        <Field label="Subject">
          <select data-testid="gt-subject-select" value={subject} onChange={(e) => { setSubject(e.target.value); setDomain(subjects[e.target.value].domains[0].key); }} className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-[#81B29A]">
            {keys.map((k) => <option key={k} value={k}>{subjects[k].name}</option>)}
          </select>
        </Field>
        <Field label="Skill area">
          <select data-testid="gt-domain-select" value={domain} onChange={(e) => setDomain(e.target.value)} className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-[#81B29A]">
            {domains.map((d) => <option key={d.key} value={d.key}>{d.name}</option>)}
          </select>
        </Field>
        <Field label="Target (next step)">
          <textarea data-testid="gt-text-input" required rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. Blend 3-phoneme words independently" className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-[#81B29A] resize-none" />
        </Field>
        <Field label="Target level (optional)">
          <select data-testid="gt-level-select" value={level} onChange={(e) => setLevel(e.target.value)} className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-[#81B29A]">
            <option value="">No specific level</option>
            {domains.find((d) => d.key === domain)?.levels.map((l) => <option key={l.level} value={l.level}>{`Level ${l.level}`}</option>)}
          </select>
        </Field>
        <button data-testid="save-group-target-btn" disabled={saving} className="w-full bg-[#4A5D6B] hover:bg-[#3A4A56] text-white rounded-full py-3 font-semibold font-['Outfit'] flex items-center justify-center gap-2 disabled:opacity-60">
          {saving && <Loader2 size={18} className="animate-spin" />} Add to {memberCount} students
        </button>
      </form>
    </Modal>
  );
}

function MembersModal({ group, allStudents, onClose, onSaved }) {
  const [picked, setPicked] = useState(new Set(group.members.map((m) => m.id)));
  const [saving, setSaving] = useState(false);
  const toggle = (id) => setPicked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const save = async () => {
    setSaving(true);
    try { await api.patch(`/groups/${group.id}`, { student_ids: Array.from(picked) }); toast.success("Members updated"); onSaved(); }
    catch (e) { toast.error("Could not update"); } finally { setSaving(false); }
  };
  return (
    <Modal title="Manage group members" onClose={onClose}>
      <div className="max-h-72 overflow-y-auto space-y-1.5 border border-slate-100 rounded-xl p-2">
        {allStudents.map((s) => (
          <button key={s.id} data-testid={`member-toggle-${s.id}`} onClick={() => toggle(s.id)} className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition ${picked.has(s.id) ? "bg-[#81B29A]/15 text-slate-800" : "hover:bg-slate-50 text-slate-600"}`}>
            <span className={`h-4 w-4 rounded border flex items-center justify-center ${picked.has(s.id) ? "bg-[#81B29A] border-[#81B29A]" : "border-slate-300"}`}>{picked.has(s.id) && <span className="text-white text-[10px]">✓</span>}</span>
            {s.name}
          </button>
        ))}
      </div>
      <button data-testid="save-members-btn" onClick={save} disabled={saving} className="mt-4 w-full bg-[#4A5D6B] hover:bg-[#3A4A56] text-white rounded-full py-3 font-semibold font-['Outfit'] flex items-center justify-center gap-2 disabled:opacity-60">
        {saving && <Loader2 size={18} className="animate-spin" />} Save members ({picked.size})
      </button>
    </Modal>
  );
}
